import { ReactNode } from 'react'
import { Know, KnowFormat, KnowStatus } from 'interfaces/Know'
import styled, { css } from 'styled-components'
import PDFIcon from 'assets/icons/pdf.svg'
import { useRouter } from 'next/router'
import { knowRoute } from 'lib/constants/routes'
import { isKnowNotIndexable } from 'lib/features/Know/knowSSR'
import Link from 'next/link'
import { Subject } from 'interfaces/Subject'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import KnowLikes from 'assets/icons/webapp_liked.svg'
import KnowViews from 'assets/icons/webapp_views.svg'
import KnowComment from 'assets/icons/webapp_comment.svg'
import theme from 'stories/utils/theme'
import { Body1, Body2, ButtonText } from '../Typography/Text'
import Stack from 'components/elements/Stack'
import FlashcardInfoPreview from './FlashcardInfo'
import KnowImpressionTracking from 'lib/tracking/KnowImpressionTracking'
import KnowFlashcardCount from 'assets/icons/webapp_flashcard_count.svg'
import { formatKnowStatus, getKnowStatusLabelColor } from 'lib/features/Know/know'
import _useTranslation from 'lib/hooks/_useTranslation'
import { storeSearchOption } from 'lib/tracking/storage'
import { SearchOptions } from 'interfaces/TrackingSnippets'
import { formatSubjectLabel } from 'lib/formatting/subjects'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import { isExpansionContentCompetitionLive } from 'lib/features/expansion'

const Container = styled.div<{ fullWidth?: boolean; isDarkTheme?: boolean }>`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 15px;
    padding-left: 5px;
    border-bottom: 3px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.white)};
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.white)};
    border-radius: ${theme.borderRadius.large};

    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  padding: 10px;
                  background-color: ${theme.colors.backgroundTransparent};
                  border-radius: ${theme.borderRadius.webappCard};
              `
            : ''}

    :hover {
        ${({ fullWidth, isDarkTheme }) =>
            isDarkTheme
                ? css`
                      background-color: ${theme.colors.backgroundTransparent};
                  `
                : fullWidth
                ? css`
                      border-radius: ${theme.borderRadius.content};
                      border-bottom: 3px solid ${isDarkTheme ? theme.colors.darkThemePrimary : theme.colors.deepSeaBlue};
                  `
                : css`
                      .title {
                          color: ${theme.colors.deepSeaBlue};
                      }
                  `}
    }
    cursor: pointer;
    .texts {
        max-width: 100%;
        display: flex;
        gap: 2px;
        flex-direction: column;
        align-items: start;
        justify-content: center;
    }
    .row {
        display: flex;
        justify-content: start;
        align-items: center;
        gap: 5px;
        .icon {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 3px;
            border: 1px solid
                ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.violetBackground)};
            background-color: ${({ isDarkTheme }) =>
                isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.violetBackground};
            border-radius: ${theme.borderRadius.small};
            padding: 0 4px;
            p {
                color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.actionGray)};
                font-size: 12px;
            }
            ${({ isDarkTheme }) =>
                isDarkTheme
                    ? css`
                          svg path {
                              fill: ${theme.colors.grayScaleSecondary} !important;
                          }
                      `
                    : ''}
        }
    }

    @media (max-width: 1250px) {
        width: auto;
    }
`

const KnowContainer = styled.div<{ isTabletSize?: boolean }>`
    width: 100px;
    height: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.large};
    cursor: pointer;
    position: relative;
    .filter {
        position: absolute;
        background-color: rgba(9, 12, 15, 0.05);
        border-radius: ${theme.borderRadius.small};
        z-index: 2;
        height: 400px;
        width: 100%;
    }
    .image {
        height: auto;
        padding: 0;
        position: relative;

        img {
            object-fit: none !important;
            object-position: top;
        }
        p {
            padding: 5px;
        }
    }

    a {
        text-decoration: none !important;
    }
`

const EmptyPreview = styled.div`
    height: 100px;
    width: 100px;
    background: ${theme.colors.deepSeaBlue};

    display: grid;
    place-content: center;
    svg {
        fill: white;
        width: 20px;
        height: 20px;
    }
`

const ThumbnailImage = styled.img``

const Headline = styled(Body1)<{ isSmall?: boolean; isDarkTheme?: boolean }>`
    white-space: nowrap;
    overflow: hidden;
    word-break: break-word;
    text-overflow: ellipsis;
    ${({ isSmall }) =>
        isSmall
            ? css`
                  max-width: 180px;
              `
            : css`
                  max-width: 450px;
              `}
    span {
        color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.deepSeaBlue)};
    }
`

const Description = styled(Body2)<{ isSmall?: boolean }>`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ isSmall }) =>
        isSmall
            ? css`
                  max-width: 180px;
              `
            : ''}
`

interface Props {
    know: Know
    referrerScreen: string
    path?: string
    isSmall?: boolean
    showStatusCard?: boolean
    search?: SearchOptions
    isDarkTheme?: boolean
}

const RelatedKnowCard = ({ know, referrerScreen, path, isSmall, showStatusCard, search, isDarkTheme }: Props) => {
    const router = useRouter()
    const localizedSubject = know ? know.subjects[0] : null
    const knowPath = path ?? knowRoute(know.slug, { referrerScreen: referrerScreen })

    const attachSearchOption = () => search && storeSearchOption(search)

    const redirectToKnow = () => {
        attachSearchOption()
        router.push(knowPath)
    }

    const KnowWrapper = (props: { children: ReactNode }) =>
        isKnowNotIndexable(know) ? (
            <div onClick={redirectToKnow}>{props.children}</div>
        ) : (
            <span onClick={attachSearchOption}>
                <Link href={knowPath} passHref prefetch={false} legacyBehavior>
                    <a>{props.children}</a>
                </Link>
            </span>
        )

    return (
        <KnowWrapper>
            {know ? <KnowImpressionTracking know={know} search={search} /> : null}
            <RelatedKnowBody
                know={know}
                localizedSubject={localizedSubject}
                isSmall={isSmall}
                showStatusCard={showStatusCard}
                isDarkTheme={isDarkTheme}
            />
        </KnowWrapper>
    )
}

interface RelatedKnowBodyProps {
    know: Know
    localizedSubject: Subject | null
    isSmall?: boolean
    showStatusCard?: boolean
    isDarkTheme?: boolean
}

const RelatedKnowBody = ({ know, localizedSubject, isSmall, showStatusCard, isDarkTheme }: RelatedKnowBodyProps) => {
    const tablet = useMedia(deviceSize.tablet)
    const knowFormatIsFlashcard = know.knowFormat === KnowFormat.Flashcards
    const { t } = _useTranslation('knows')
    const { locale } = useRouter()
    const countryLanguageConfig = getCountryLanguageConfig(locale)
    const isExpansion = isExpansionContentCompetitionLive(countryLanguageConfig)

    const showKnowStatus = showStatusCard && !(knowFormatIsFlashcard && know.status === KnowStatus.Draft)

    return (
        <>
            <Container fullWidth={!isSmall} isDarkTheme={isDarkTheme}>
                <div className="texts">
                    <Headline
                        fontFamily="var(--font-inter)"
                        fontWeight={400}
                        color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.headlineDark}
                        isSmall={isSmall}
                        isDarkTheme={isDarkTheme}
                        className="title"
                    >
                        {localizedSubject && !isSmall ? <span>{`${formatSubjectLabel(localizedSubject)} - `}</span> : null}
                        {know.seoTitle ?? know.title}
                    </Headline>
                    <Stack gutter={5}>
                        <Description
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                            color={isDarkTheme ? theme.colors.white : theme.colors.actionGray}
                            isSmall={isSmall}
                        >
                            {know.seoDescription ?? know.description}
                        </Description>
                        <div className="row">
                            <div className="icon">
                                <KnowLikes />
                                <ButtonText fontFamily="var(--font-inter)" fontWeight={600} color={theme.colors.actionGray}>
                                    {know.likes}
                                </ButtonText>
                            </div>
                            <div className="icon">
                                <KnowViews />
                                <ButtonText fontFamily="var(--font-inter)" fontWeight={600} color={theme.colors.actionGray}>
                                    {know.views}
                                </ButtonText>
                            </div>
                            <div className="icon">
                                <KnowComment />
                                <ButtonText fontFamily="var(--font-inter)" fontWeight={600} color={theme.colors.actionGray}>
                                    {know.commentsCount}
                                </ButtonText>
                            </div>
                            {knowFormatIsFlashcard ? (
                                <div className="icon">
                                    <KnowFlashcardCount />
                                    <ButtonText
                                        fontFamily="var(--font-inter)"
                                        fontWeight={600}
                                        color={theme.colors.actionGray}
                                    >
                                        {know.flashcardCount}
                                    </ButtonText>
                                </div>
                            ) : null}
                            {showKnowStatus ? (
                                <div className="icon">
                                    <ButtonText
                                        fontFamily="var(--font-inter)"
                                        fontWeight={600}
                                        color={getKnowStatusLabelColor(know.status)}
                                    >
                                        {formatKnowStatus(t, know.status, { hasContentCampaignRunning: isExpansion })}
                                    </ButtonText>
                                </div>
                            ) : null}
                        </div>
                    </Stack>
                </div>
                <KnowContainer isTabletSize={tablet}>
                    <div className="image">
                        <div className="filter" />

                        {know?.thumbnailSmallUrl ? (
                            <ThumbnailImage
                                src={know.thumbnailSmallUrl}
                                alt={`Know ${know.seoTitle ?? know.title} thumbnail`}
                                width={100}
                                height={100}
                                loading="lazy"
                            />
                        ) : (
                            <EmptyPreview>
                                {knowFormatIsFlashcard ? <FlashcardInfoPreview know={know} isSmall /> : <PDFIcon />}
                            </EmptyPreview>
                        )}
                    </div>
                </KnowContainer>
            </Container>
        </>
    )
}

export default RelatedKnowCard
